import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Stack,
    useTheme,
    alpha
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    CleaningServices as CleaningServicesIcon,
    Science as ScienceIcon,
    Speed as SpeedIcon,
    Thermostat as ThermostatIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';
import { useNavigate } from 'react-router-dom';

export default function AreaCheckList({ checklist, setChecklist, lineLogId, createLineLog, onChecklistUpdate, lineTimeFormat = '12hour' }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [formData, setFormData] = useState({
        line_sanitized: false,
        equipment_cleaned: false,
        safety_check_complete: false,
        area_cleared: false,
        supplies_verified: false,
        area_cleaning_confirmed: false,
        equipment_calibrated: false,
        environmental_conditions_verified: false,
        ppe_verified: false,
        batch_record_prepared: false,
        quality_standards_reviewed: false,
        work_area_cleared: false,
        operator_name: '',
        operator_initials: '',
        operator_signature: '',
        operator_position: '',
        supervisor_name: '',
        supervisor_signature: '',
        cleaning_start_time: null,
        cleaning_end_time: null,
        cleaning_chemicals_used: '',
        additional_notes: ''
    });

    const [operatorSignatureDialog, setOperatorSignatureDialog] = useState(false);
    const [previewName, setPreviewName] = useState('');

    useEffect(() => {
        if (checklist && checklist.id) {
            setFormData({
                line_sanitized: checklist.line_sanitized || false,
                equipment_cleaned: checklist.equipment_cleaned || false,
                safety_check_complete: checklist.safety_check_complete || false,
                area_cleared: checklist.area_cleared || false,
                supplies_verified: checklist.supplies_verified || false,
                area_cleaning_confirmed: checklist.area_cleaning_confirmed || false,
                equipment_calibrated: checklist.equipment_calibrated || false,
                environmental_conditions_verified: checklist.environmental_conditions_verified || false,
                ppe_verified: checklist.ppe_verified || false,
                batch_record_prepared: checklist.batch_record_prepared || false,
                quality_standards_reviewed: checklist.quality_standards_reviewed || false,
                work_area_cleared: checklist.work_area_cleared || false,
                operator_name: checklist.operator_name || '',
                operator_initials: checklist.operator_initials || '',
                operator_signature: checklist.operator_signature || '',
                operator_position: checklist.operator_position || '',
                supervisor_name: checklist.supervisor_name || '',
                supervisor_signature: checklist.supervisor_signature || '',
                cleaning_start_time: checklist.cleaning_start_time ? dayjs(checklist.cleaning_start_time) : null,
                cleaning_end_time: checklist.cleaning_end_time ? dayjs(checklist.cleaning_end_time) : null,
                cleaning_chemicals_used: checklist.cleaning_chemicals_used || '',
                additional_notes: checklist.additional_notes || ''
            });
        }
    }, [checklist]);

    useEffect(() => {
        // Clear preview when dialog opens
        if (operatorSignatureDialog) {
            setPreviewName('');
        }
    }, [operatorSignatureDialog]);

    const handleSave = async () => {
        // Check permissions before proceeding
        if (!canEdit) {
            console.error('You do not have permission to save area checklist.');
            return;
        }

        try {
            const logId = await createLineLog();
            if (!logId) {
                console.error('Failed to create or get line log ID for checklist');
                return;
            }

            const saveData = {
                ...formData,
                cleaning_start_time: formData.cleaning_start_time ? formData.cleaning_start_time.toISOString() : null,
                cleaning_end_time: formData.cleaning_end_time ? formData.cleaning_end_time.toISOString() : null,
                line_log_id: logId,
                completed_at: dayjs().toISOString()
            };

            let result;
            if (checklist && checklist.id) {
                const { data, error } = await supabase
                    .from('production_line_log_checklists')
                    .update(saveData)
                    .eq('id', checklist.id)
                    .select()
                    .single();
                if (error) {
                    console.error('Error updating checklist:', error);
                    return;
                }
                result = data;
            } else {
                // Get company_id from the user session
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    // Get company_id from user's profile or company_users table
                    const { data: companyUser } = await supabase
                        .from('company_users')
                        .select('company_id')
                        .eq('user_id', user.id)
                        .eq('status', 'active')
                        .single();
                    
                    if (companyUser) {
                        saveData.company_id = companyUser.company_id;
                    }
                }

                const { data, error } = await supabase
                    .from('production_line_log_checklists')
                    .insert(saveData)
                    .select()
                    .single();
                if (error) {
                    console.error('Error inserting checklist:', error);
                    return;
                }
                result = data;
            }

            setChecklist(result);
            
            // Refresh parent component data to show updated completion status
            if (onChecklistUpdate) {
                await onChecklistUpdate();
            }
        } catch (error) {
            console.error('Error saving checklist:', error);
        }
    };

    const handleOperatorSignature = () => {
        const signatureName = document.getElementById('operator-signature-name')?.value || '';
        const signaturePosition = document.getElementById('operator-signature-position')?.value || 'Production Operator';
        
        if (!signatureName.trim()) {
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            operator_signature: signatureName,
            operator_position: signaturePosition,
            operator_name: signatureName
        }));
        
        setOperatorSignatureDialog(false);
    };

    const checklistItems = [
        { key: 'line_sanitized', label: 'Production Line Sanitized & Ready', icon: <CleaningServicesIcon /> },
        { key: 'equipment_cleaned', label: 'Equipment Cleaned & Inspected', icon: <ScienceIcon /> },
        { key: 'safety_check_complete', label: 'Safety Protocols Verified', icon: <SecurityIcon /> },
        { key: 'area_cleared', label: 'Work Area Cleared of Previous Materials', icon: <CheckCircleIcon /> },
        { key: 'supplies_verified', label: 'Required Supplies & Materials Verified', icon: <AssignmentIcon /> },
        { key: 'equipment_calibrated', label: 'Equipment Calibrated & Ready', icon: <SpeedIcon /> },
        { key: 'environmental_conditions_verified', label: 'Environmental Conditions Verified', icon: <ThermostatIcon /> },
        { key: 'ppe_verified', label: 'PPE & Safety Gear Verified', icon: <PersonIcon /> },
        { key: 'quality_standards_reviewed', label: 'Quality Standards Reviewed', icon: <CheckCircleIcon /> },
        { key: 'work_area_cleared', label: 'Work Area Cleared & Ready', icon: <CheckCircleIcon /> }
    ];

    const isAllChecked = checklistItems.every(item => formData[item.key]);

    // Permission check - must be after all hooks
    if (!hasPermission('production.line.log.checklist.view', 'read')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '60vh',
                textAlign: 'center',
                width: '100%',
                px: 3,
                py: 8
            }}>
                <Card sx={{
                    p: 6,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 500,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 4,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <AssignmentIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h4" sx={{ 
                        mb: 2, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 3,
                        fontWeight: 500,
                        fontSize: '1.1rem'
                    }}>
                        Area Checklist Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        mb: 4,
                        fontSize: '1rem',
                        maxWidth: 400,
                        mx: 'auto'
                    }}>
                        You don't have permission to view the Area Checklist. 
                        Contact your administrator to request access.
                    </Typography>
                </Card>
            </Box>
        );
    }

    // Check edit permissions  
    const canEdit = hasPermission('production.line.log.checklist.edit', 'write');

    return (
        <Box>
            {/* Status Alert */}
            <Alert 
                severity={isAllChecked ? "success" : "warning"}
                sx={{ 
                    mb: 4,
                    border: '1px solid',
                    borderColor: isAllChecked ? theme.palette.success.main : theme.palette.warning.main,
                    borderRadius: 3,
                    fontWeight: 500
                }}
            >
                {isAllChecked 
                    ? "All checklist items completed! Ready to proceed with production."
                    : "Please complete all checklist items before starting the production process."
                }
            </Alert>

            <Grid container spacing={3}>
                {/* Completion Overview Widgets */}
                <Grid item xs={3}>
                    <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        height: 120,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ 
                                fontWeight: 500, 
                                color: isAllChecked ? theme.palette.success.main : theme.palette.warning.main,
                                mb: 1
                            }}>
                                {((checklistItems.filter(item => formData[item.key]).length / checklistItems.length) * 100).toFixed(0)}%
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.grey[800], letterSpacing: 0.5 }}>
                                COMPLETION
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={3}>
                    <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        height: 120,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 1 }}>
                                {checklistItems.filter(item => formData[item.key]).length}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.grey[800], letterSpacing: 0.5 }}>
                                COMPLETED
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={3}>
                    <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        height: 120,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ 
                                fontWeight: 500, 
                                color: theme.palette.warning.main,
                                mb: 1
                            }}>
                                {checklistItems.filter(item => !formData[item.key]).length}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.grey[800], letterSpacing: 0.5 }}>
                                PENDING
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={3}>
                    <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        height: 120,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ 
                                fontWeight: 500, 
                                color: isAllChecked ? theme.palette.success.main : theme.palette.error.main,
                                mb: 1
                            }}>
                                {isAllChecked ? '✓' : '✗'}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.grey[800], letterSpacing: 0.5 }}>
                                STATUS
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Checklist Items */}
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ 
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Area Preparation Checklist
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Complete all items before starting production
                                        </Typography>
                                    </Box>
                                </Box>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        if (!canEdit) return;
                                        const allChecked = {};
                                        checklistItems.forEach(item => {
                                            allChecked[item.key] = true;
                                        });
                                        setFormData({...formData, ...allChecked});
                                    }}
                                    disabled={isAllChecked || !canEdit}
                                    sx={{
                                        borderRadius: 3,
                                        borderColor: theme.palette.grey[600],
                                        color: theme.palette.grey[700],
                                        fontWeight: 500,
                                        '&:hover': {
                                            borderColor: theme.palette.grey[700],
                                            backgroundColor: alpha(theme.palette.grey[200], 0.5)
                                        },
                                        '&:disabled': {
                                            borderColor: theme.palette.grey[400],
                                            color: theme.palette.grey[500]
                                        }
                                    }}
                                >
                                    Check All
                                </Button>
                            </Box>

                            <Stack spacing={2}>
                                {checklistItems.map((item) => (
                                    <Box 
                                        key={item.key}
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            p: 2,
                                            border: `1px solid ${theme.palette.grey[300]}`,
                                            borderRadius: 3,
                                            bgcolor: formData[item.key] ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                borderColor: theme.palette.grey[600],
                                                transform: 'translateX(4px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ color: formData[item.key] ? theme.palette.success.main : theme.palette.warning.main, fontSize: 24 }}>
                                                {item.icon}
                                            </Box>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.grey[800] }}>
                                                {item.label}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Chip 
                                                label={formData[item.key] ? 'COMPLETED' : 'PENDING'}
                                                color={formData[item.key] ? 'success' : 'warning'}
                                                size="small"
                                                sx={{ 
                                                    fontWeight: 500,
                                                    minWidth: 100
                                                }}
                                            />
                                            <Checkbox
                                                checked={formData[item.key]}
                                                onChange={(e) => canEdit ? setFormData({
                                                    ...formData,
                                                    [item.key]: e.target.checked
                                                }) : null}
                                                disabled={!canEdit}
                                                sx={{
                                                    color: formData[item.key] ? theme.palette.success.main : theme.palette.warning.main,
                                                    '&.Mui-checked': { color: theme.palette.success.main },
                                                    transform: 'scale(1.3)'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Operator Information Card */}
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <PersonIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Operator Information
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Checklist operator identification and signature
                                    </Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    {formData.operator_signature ? (
                                        <Box sx={{ 
                                            border: `1px solid ${theme.palette.grey[400]}`, 
                                            p: 3, 
                                            borderRadius: 3, 
                                            bgcolor: alpha(theme.palette.grey[50], 0.8),
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            }
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                Operator Signature:
                                            </Typography>
                                            <Box sx={{ 
                                                fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                fontSize: '28px', 
                                                color: theme.palette.grey[800],
                                                mb: 1,
                                                textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                                            }}>
                                                {formData.operator_signature}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                <strong>Position:</strong> {formData.operator_position || 'Production Operator'}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ 
                                            border: `1px solid ${theme.palette.grey[400]}`, 
                                            p: 3, 
                                            borderRadius: 3, 
                                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                                            textAlign: 'center'
                                        }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No signature recorded
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Click "Add Signature" to sign this checklist
                                            </Typography>
                                        </Box>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', justifyContent: 'center' }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={() => canEdit ? setOperatorSignatureDialog(true) : null}
                                            disabled={!canEdit}
                                            fullWidth
                                            size="large"
                                            sx={{
                                                borderRadius: 3,
                                                borderColor: theme.palette.grey[600],
                                                color: theme.palette.grey[700],
                                                fontWeight: 500,
                                                '&:hover': {
                                                    borderColor: theme.palette.grey[700],
                                                    backgroundColor: alpha(theme.palette.grey[200], 0.5)
                                                }
                                            }}
                                        >
                                            {formData.operator_signature ? 'Change Signature' : 'Add Signature'}
                                        </Button>
                                        {formData.operator_signature && (
                                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                Signature recorded
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Additional Notes"
                                        value={formData.additional_notes}
                                        onChange={(e) => canEdit ? setFormData({...formData, additional_notes: e.target.value}) : null}
                                        disabled={!canEdit}
                                        placeholder={canEdit ? "Any additional observations or notes..." : "Additional notes (read-only)"}
                                        sx={{
                                            flexGrow: 1,
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Action Bar */}
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            {checklist && checklist.completed_at && (
                                <Chip 
                                    icon={<ScheduleIcon />}
                                    label={`Completed: ${dayjs(checklist.completed_at).format(lineTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}`}
                                    sx={{
                                        backgroundColor: theme.palette.grey[700],
                                        color: 'white',
                                        fontWeight: 500,
                                        '& .MuiChip-icon': { color: 'white' }
                                    }}
                                />
                            )}
                        </Box>
                        <Button 
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<CheckCircleIcon />}
                            disabled={!formData.operator_signature || !canEdit}
                            size="large"
                            sx={{
                                borderRadius: 3,
                                px: 4,
                                py: 1.5,
                                background: isAllChecked ? theme.palette.success.main : theme.palette.grey[700],
                                color: 'white',
                                fontWeight: 500,
                                '&:hover': {
                                    background: isAllChecked ? theme.palette.success.dark : theme.palette.grey[800],
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                },
                                '&:disabled': {
                                    background: theme.palette.grey[400],
                                    color: theme.palette.grey[600]
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {isAllChecked ? "Complete Checklist" : "Save Progress"}
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {/* Operator Signature Dialog */}
            <Dialog 
                open={operatorSignatureDialog} 
                onClose={() => setOperatorSignatureDialog(false)} 
                maxWidth="sm" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                    fontWeight: 500
                }}>
                    Add Operator Signature
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mt: 2, color: theme.palette.grey[700] }}>
                                Enter your signature information:
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                id="operator-signature-name"
                                label="Signature Name"
                                fullWidth
                                autoFocus
                                required
                                placeholder="Enter your full name"
                                onChange={(e) => setPreviewName(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                id="operator-signature-position"
                                label="Position/Title"
                                fullWidth
                                defaultValue="Production Operator"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ 
                                mt: 2, 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: alpha(theme.palette.grey[50], 0.8)
                            }}>
                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                                    Preview: Your signature will appear in script font on the checklist
                                </Typography>
                                <Box sx={{ 
                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                    fontSize: '28px', 
                                    color: theme.palette.grey[800],
                                    mt: 2,
                                    mb: 1,
                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {previewName || 'Preview will appear here'}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Button 
                        onClick={() => setOperatorSignatureDialog(false)}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            color: theme.palette.grey[600],
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[200], 0.5)
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleOperatorSignature} 
                        variant="contained"
                        sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1,
                            background: alpha(theme.palette.grey[600], 0.9),
                            color: 'white',
                            fontWeight: 500,
                            '&:hover': {
                                background: theme.palette.grey[700]
                            }
                        }}
                    >
                        Save Signature
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}